/// <reference types="node-fetch">
import crossFetch from 'cross-fetch';
import normalize from 'normalize-object';

import inMemoryJWT from 'services/auth/inMemoryJWT';
import notifications from 'lib/notifications';
import utils, { encryption, decryption } from 'lib/utils';
import config from 'config';
const { isApiEncryptionEnabled } = config;
import queryStringParser from 'query-string';

const API_URL = process.env.REACT_APP_BASE_URL;
let lastNetworkErrorAt = 0;
const NETWORK_ERROR_COOLDOWN_MS = 3000; // suppress duplicate network error handling bursts

export type ResponseCheck<T> = {
  data: T;
  errors: Object | null;
  meta: Object | null;
};

const openFetch = function (request: string, opts?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const signal = controller.signal;

  const requestOptions = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: inMemoryJWT.getToken(),
    },
    ...opts,
  };

  const reqString = requestOptions?.['body'];
  let urlWithEncryptedQs;
  if (isApiEncryptionEnabled === 'true') {
    const parsedUrl = new URL(`${request?.includes('http') ? '' : API_URL}${request}`);
    if (parsedUrl.search) {
      const queryParams = parsedUrl.search.split('?')[1];
      parsedUrl.search = '';
      const isAlreadyEncrypted = decryption(queryParams);
      urlWithEncryptedQs =
        isApiEncryptionEnabled === 'true'
          ? isAlreadyEncrypted
            ? request
            : `${parsedUrl?.pathname}?${utils.getQs(queryStringParser.parse(queryParams))}`
          : request;
    }
  }

  setTimeout(() => {
    controller.abort();
  }, 30 * 1000);
  const requestUrl = urlWithEncryptedQs
    ? `${urlWithEncryptedQs?.includes('http') ? '' : API_URL}${urlWithEncryptedQs}`
    : `${request?.includes('http') ? '' : API_URL}${request}`;
  return isApiEncryptionEnabled === 'true'
    ? crossFetch(
        requestUrl,
        requestOptions?.body
          ? { ...requestOptions, body: encryption(reqString), signal }
          : { ...requestOptions, signal },
      )
    : crossFetch(requestUrl, { ...requestOptions, signal });
};

async function doFetching<T>(urlFragment: string, opts: RequestInit = { method: 'GET' }): Promise<ResponseCheck<T>> {
  try {
    const res = await openFetch(urlFragment, opts);

    // if (res.headers?.map['content-type'] !== 'application/json; charset=utf-8') {
    //   notifications.show({
    //     type: 'error',
    //     message: `invalid reponse: content-type` + res.headers?.map['content-type'],
    //     key: 'fetch-errors',
    //   });
    // }

    // Safely parse/decrypt; attach status if parsing fails so error handlers can act on 511/401
    let rawResponse: any;
    try {
      rawResponse = isApiEncryptionEnabled === 'true' ? await utils.processApiRes(res) : await res.json();
    } catch (parseErr: any) {
      // Re-throw including HTTP status for downstream handlers (e.g., 511 refresh logic)
      const enriched = { ...(parseErr || {}), status: res.status, errorCode: res.status } as any;
      throw enriched;
    }
    const response = normalize(rawResponse, 'snake');

    if (res.status >= 400) {
      const errorMessage = response.message || response.title || '';
      const displayError = utils.getErrMessage(response.data) + '. ' + errorMessage;
      const lowerMsg = String(errorMessage).toLowerCase();
      const isJwtNotFound = lowerMsg.includes('jwt token not found in the system');
      const isAuth511 = res.status === 511;
      const isAuth401 = res.status === 401;
      const isAuth403 = res.status === 403;
      const isSessionExpiredMsg = lowerMsg.includes('session expired');
      // Suppress toast for JWT-not-found / 511. Let auth flow handle silently.
      if (!(isJwtNotFound || isAuth511 || isAuth401 || isAuth403 || isSessionExpiredMsg)) {
        notifications.show({
          type: 'error',
          message: displayError,
          key: 'fetch-errors-400',
        });
      }
      throw {
        errors: response.data,
        message: errorMessage,
        stat: response.stat,
        errorCode: res.status,
        status: res.status,
      };
    }

    // return { data: response.data, errors: null, meta: null };
    return { data: isApiEncryptionEnabled === 'true' ? response : response.data, errors: null, meta: null };
  } catch (networkError: any) {
    // One-time retry on network/timeout
    const message = String(networkError?.message || '').toLowerCase();
    const isNetworkIssue = message.includes('network request failed') || message.includes('aborted');
    const now = Date.now();
    const withinCooldown = now - lastNetworkErrorAt < NETWORK_ERROR_COOLDOWN_MS;
    const canRetryOnce = isNetworkIssue && !withinCooldown;
    if (isNetworkIssue) {
      // Show a single offline toast, then suppress for the next few seconds
      if (!withinCooldown) {
        lastNetworkErrorAt = now;
        notifications.show({
          type: 'error',
          message: 'Unable to process request. Please check your internet connection',
        });
      }
    }
    if (canRetryOnce) {
      // If offline, wait for a short time or until online event
      const waitForOnline = () =>
        new Promise<void>((resolve) => {
          if (typeof window !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
            const onBack = () => {
              window.removeEventListener('online', onBack);
              resolve();
            };
            // Fallback timeout of 2s to avoid hanging forever
            const t = setTimeout(() => {
              window.removeEventListener('online', onBack);
              resolve();
            }, 2000);
            window.addEventListener('online', () => {
              clearTimeout(t);
              onBack();
            });
          } else {
            // If we are online, just backoff briefly
            setTimeout(() => resolve(), 500);
          }
        });
      await waitForOnline();
      try {
        const retryRes = await openFetch(urlFragment, opts);
        let rawRetry: any;
        try {
          rawRetry = isApiEncryptionEnabled === 'true' ? await utils.processApiRes(retryRes) : await retryRes.json();
        } catch (retryParseErr: any) {
          const enriched = { ...(retryParseErr || {}), status: retryRes.status, errorCode: retryRes.status } as any;
          throw enriched;
        }
        const retryNorm = normalize(rawRetry, 'snake');
        if (retryRes.status >= 400) {
          const errorMessage = retryNorm.message || retryNorm.title || '';
          const displayError = utils.getErrMessage(retryNorm.data) + '. ' + errorMessage;
          const lowerMsg = String(errorMessage).toLowerCase();
          const isJwtNotFound = lowerMsg.includes('jwt token not found in the system');
          const isAuth511 = retryRes.status === 511;
          const isAuth401 = retryRes.status === 401;
          const isAuth403 = retryRes.status === 403;
          const isSessionExpiredMsg = lowerMsg.includes('session expired');
          if (!(isJwtNotFound || isAuth511 || isAuth401 || isAuth403 || isSessionExpiredMsg)) {
            notifications.show({ type: 'error', message: displayError, key: 'fetch-errors-400' });
          }
          throw {
            errors: retryNorm.data,
            message: errorMessage,
            stat: retryNorm.stat,
            errorCode: retryRes.status,
            status: retryRes.status,
          };
        }
        return { data: isApiEncryptionEnabled === 'true' ? retryNorm : retryNorm.data, errors: null, meta: null };
      } catch (retryErr: any) {
        const errors = await utils.processApiErrors(retryErr);
        throw errors;
      }
    }
    const errors = await utils.processApiErrors(networkError);
    throw errors;
  }
}

export const fetchList = async <T>(urlFragment: string): Promise<ResponseCheck<T[]>> => {
  const response = await doFetching<T[]>(urlFragment);
  return response;
};

export const fetchOne = async <T>(urlFragment: string): Promise<ResponseCheck<T>> => {
  const response = await doFetching<T>(urlFragment);
  return response;
};

export const create = async <T>(urlFragment: string, payload: T): Promise<ResponseCheck<T>> => {
  const opts = { body: JSON.stringify(payload), method: 'POST' };
  const response = await doFetching<T>(urlFragment, opts);
  return response;
};

export const update = async <T>(urlFragment: string, payload: T): Promise<ResponseCheck<T>> => {
  const opts = { body: JSON.stringify(payload), method: 'PUT' };
  const response = await doFetching<T>(urlFragment, opts);
  return response;
};

// export const destroy = async <T>(urlFragment: string): Promise<ResponseCheck<T>> => {
//   const opts = { method: 'DELETE' };
//   const response = await doFetching<T>(urlFragment, opts);
//   return response;
// };

export default { fetchList, fetchOne, create, update };
